import type { EventCalendarRangeDates } from '@repo/hooks/useEventCalendarRangeDates';
import type { MeetingPublic } from '@repo/types/meetings';
import type { Dispatch, SetStateAction } from 'react';

import { useDeleteMeeting } from '@repo/api/meeting/useDeleteMeeting';
import { baseErrorMessages } from '@repo/lib/messages/baseErrorMessages';
import { format } from 'date-fns';
import { Calendar, Dot } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogBody, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/dialog';
import { FeaturedIcon } from '@/components/ui/featured-icon';
import { toasterMessageHandler } from '@/lib/toaster-message-handler';

interface DeleteMeetingProps {
  open: boolean;
  onOpenChange: Dispatch<SetStateAction<boolean>>;
  callback?: () => void;
  event?: MeetingPublic;
  queryParams?: EventCalendarRangeDates;
}

export default function DeleteMeeting(deleteMeetingProps: DeleteMeetingProps) {
  const { open, onOpenChange, callback, event, queryParams } = deleteMeetingProps;
  const { id, startDate, endDate, name, type } = event || {};

  const { mutateAsync, reset } = useDeleteMeeting(queryParams);

  const onSubmit = () => {
    if (!id) {
      return;
    }
    mutateAsync(id)
      .then(() => {
        callback?.();
        onClose();
      })
      .catch((error) => {
        toasterMessageHandler(baseErrorMessages, error);
      });
  };

  const onClose = () => {
    onOpenChange(false);
    reset();
  };

  return (
    <AlertDialog onOpenChange={onOpenChange} open={open}>
      <AlertDialogContent className={'sm:max-w-96'}>
        <AlertDialogBody>
          <AlertDialogHeader className={'border-border-primary border-b'}>
            <FeaturedIcon size={'lg'}>
              <Calendar />
            </FeaturedIcon>
            <div className={'flex min-w-0 grow flex-col gap-0.5'}>
              <AlertDialogTitle>Usunąć wybrane spotkanie</AlertDialogTitle>
              <p className={'text-text-tertiary text-xs'}>Usuń wybrane spotkanie z kalendarza.</p>
            </div>
          </AlertDialogHeader>
          <div className={'flex flex-col gap-3 overflow-auto p-4'}>
            <div className={'flex min-h-9 items-center'}>
              <span className={'text-muted-foreground min-w-15'}>Nazwa: </span>
              <p className="text-sm">{name}</p>
            </div>
            <div className={'flex min-h-9 items-center'}>
              <span className={'text-muted-foreground min-w-15'}>Data: </span>
              <p className={'flex items-center text-sm'}>
                {startDate ? format(new Date(startDate), 'dd MMM yyyy, HH:mm') : ''}
                <Dot className={'size-4'} />
                {endDate ? format(new Date(endDate), 'dd MMM yyyy, HH:mm') : ''}
              </p>
            </div>
            <div className={'flex min-h-9 items-center'}>
              <span className={'text-muted-foreground min-w-15'}>Typ:</span>
              <Badge variant={'orange'}>{type === 'remote' ? 'Zdalnie' : 'Stacjonarnie'}</Badge>
            </div>
          </div>
        </AlertDialogBody>
        <AlertDialogFooter className={'border-border-primary dark:bg-card bg-accent border-t'}>
          <AlertDialogCancel variant={'tertiary'} onClick={onClose}>
            Zamknij
          </AlertDialogCancel>
          <AlertDialogAction onClick={onSubmit}>Potwierdź</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
